import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';
import GuiList from '../../components/gui/list';
import UserProfileInline from '../../components/user/profileInline';
import Camera from '../../components/gui/camera';

import localUtil from '../../lib/util';
import { getStageTypeDisplay, getStageTimeLabel, pickCurrentDisciplineClasses, conventionYearOf, validateConventionScores, parseRubicReportErrors } from '../../lib/conventionResults';
import { syncShooterFromRubic } from '../../lib/conventionRubicSync';
import { branchIdForWeaponClass } from '../../lib/rubicClub';
import { QRCodeSVG } from 'qrcode.react';
import NotMemberBadge from './notMemberBadge';
import NsfTransferProgress from './nsfTransferProgress';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';
import { requiredStarts } from '../../lib/conventionPayState';


@withText(props => ({
    searchTitle: <Text id='input.search-for-user'>Search for user</Text>,
}))
@observer
class ConventionViewStageAdmin extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            loadingUser: {},
        };
    }

    newRound = async e => {
        const { loadAll = () => {} } = this.props;
        const { type } = e.target.closest('button').dataset;
        const { conventionStore } = this.props.stores;
        await conventionStore.newRound(type);
        await loadAll();
    }

    editStage = e => {
        const { id, md5, idx } = e.target.closest('button').dataset;
        const { drawerLevel = 1, loadAll = () => {} } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('editConventionStage', {
            id,
            idx,
            md5,
            height: drawerHeightMedium,
            callback: () => {
                loadAll();
            },
        }, drawerLevel + 2);
    }

    deleteStage = async e => {
        const dialogAction = beginDialogAction(this, 'deleteStage', () => [
            this.props.stores.conventionStore.convention?.id,
        ]);
        if (!dialogAction) return false;
        try {
            const { md5, name, count } = e.target.closest('button').dataset;
            const { loadAll = () => {} } = this.props;
            const { appState, conventionStore } = this.props.stores;
            const num = parseInt(count, 10) || 0;
            const warn = num > 0 ? `\n\n${name} har ${num} påmeldt${num === 1 ? '' : 'e'} som mistes.` : '';
            if (!((await appConfirm(`Slette ${name}? Dette kan ikke angres.${warn}`)) && dialogAction.isCurrent())) {
                return;
            }
            const response = await conventionStore.deleteStage(md5);
            if (response && response.errorCode) {
                appState.addToast({ type: 'error', message: response.message || 'Kunne ikke slette runden' }, 5000);
                return;
            }
            await loadAll();
        } finally {
            dialogAction.finish();
        }
    }

    addNewParticipant = async e => {
        const { loadAll = () => {} } = this.props;
        const { id } = e.target.closest('button').dataset;
        const { conventionStore, userStore } = this.props.stores;
        const { searchUsersResult } = userStore;
        const user = searchUsersResult.find(user => user.id === parseInt(id, 10));
        // Pass isAdmin=true so admins are exempt from the 2-hour gap rule
        await conventionStore.newParticipant(user, null, true);
        await loadAll();
    }

    editConvention = e => {
        const { id, title } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
        const { loadAll = () => {} } = this.props;
		const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
		const { drawerHeightLarge } = appState;
		appState.openDrawer('editConvention', {
            id,
			height: drawerHeightLarge,
            callback: () => {
                loadAll();
            },
		}, drawerLevel + 2);
	}

    openBulkGenerateStages = () => {
        const { drawerLevel = 1 } = this.props;
        const { loadAll = () => {} } = this.props;
        const { appState, conventionStore } = this.props.stores;
        conventionStore.deleteReloadTimer();
        const { drawerHeightLarge } = appState;
        appState.openDrawer('conventionBulkGenerateStages', {
            height: drawerHeightLarge,
            callback: async () => {
                await loadAll();
            },
        }, drawerLevel + 2);
    }

    getRubicCompetitionByNumber = async () => {
        this.setState({ loading: true });
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const { nsfNumber } = convention;

        const response = await appState.rubicGetCompetitionByNumber(nsfNumber);
        const { data, status } = response;
        console.log('getRubicCompetitionByNumber', response);

        if (status === 200) {
            if (data && data.id) {
                // Update convention with Rubic data.
                const { id: nsfId } = data;
                await conventionStore.saveField(convention.id, 'nsfId', nsfId, true);
                appState.addToast({
                    type: 'success',
                    message: `Rubic competition found! ${nsfNumber}`
                }, 5000);
                console.log('getRubicCompetitionByNumber', 'Updated convention with Rubic data', { nsfId });
            } else {
                appState.addToast({
                    type: 'error',
                    message: `Rubic competition not found! ${nsfNumber}`,
                }, 5000)
                console.error('getRubicCompetitionByNumber', 'No data found for Rubic competition with number', nsfNumber);
                // appState.showToast('error', <Text id='convention.rubic-competition-not-found'>Rubic competition not found</Text>);
            }
        } else {
            appState.addToast({
                type: 'error',
                message: `Failed to fetch Rubic competition: ${response.status} ${response.statusText}`
            }, 5000);
        }
        this.setState({ loading: false });
    }

    // getRubicPersonSearch = async () => {
    //     const { appState } = this.props.stores;
    //     const response = await appState.rubicGetPersonSearch({ name: 'Per godkjenner hansen'});
    //     console.log('getRubicPersonSearch', response);
    // }

    // Sync one participant from Rubic. Returns { ok, notFound, ... } so callers
    // (single button + "sync all") can report progress. Does NOT reload — the
    // caller decides when to refresh.
    //
    // The shooter's classifications (nsfDiciplineClasses) are stored deduped to
    // the convention year — one current class per discipline — and sent via
    // `nsfDiciplineClasses-overwrite` so the backend REPLACES the stored array,
    // dropping the accumulated old-year classes.
    //
    // alwaysResolveClub:true — Rubic is the source of truth for club membership, so re-resolve and
    // OVERWRITE the shooter's nsfClubId/nsfClubNumber/sportsClub from their active membership even
    // when a (possibly stale/wrong) club is already stored. This is what makes "Sync all from Rubic"
    // self-heal bad club data — a wrong nsfClubId makes Rubic reject the whole result batch (400
    // "Person is not member in organization").
    syncParticipant = async (participant, conventionYear) => {
        const { appState, userStore, conventionStore } = this.props.stores;
        if (!participant) {
            return { ok: false };
        }
        // Branch (gren) of the participant's weapon class: the club is the one the shooter
        // represents in that branch (branchOrganizations), not the first active membership.
        await conventionStore.loadDisciplineMap();
        const branchId = branchIdForWeaponClass(conventionStore.disciplineMap, participant.class);
        const res = await syncShooterFromRubic({
            participant,
            conventionYear,
            appState,
            userStore,
            alwaysResolveClub: true,
            branchId,
        });
        return {
            ok: res.ok,
            notFound: res.notFound,
            participant,
            rubicPersonId: res.rubicPersonId,
            name: res.name,
            sportsClub: res.club ? res.club.sportsClub : undefined,
        };
    }

    lookupUser = async e => {
        const { id } = e.target.closest('button').dataset;
        const { loadingUser = {} } = this.state;
        loadingUser[id] = true;
        this.setState({ loadingUser });

        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const participant = (convention.participants || []).find(p => p.id === parseInt(id, 10));
        const result = await this.syncParticipant(participant, conventionYearOf(convention));
        if (result.ok) {
            appState.addToast({
                type: 'success',
                message: `Rubic user found! ${result.rubicPersonId} - ${result.name}${result.sportsClub ? ` - ${result.sportsClub}` : ''}`,
            }, 5000);
        } else if (result.notFound) {
            appState.addToast({
                type: 'error',
                message: `Rubic user not found! ${participant.firstname} ${participant.lastname} - ${participant.cellphone} - ${participant.email}`,
            }, 5000);
        }

        const { loadAll = () => {} } = this.props;
        await loadAll();

        delete loadingUser[id];
        this.setState({ loadingUser });
    }

    // Bulk-sync every participant from Rubic, sequentially (one Rubic round-trip
    // each — sequential avoids hammering the external API). Updates a progress
    // counter and reloads once at the end.
    lookupAllUsers = async () => {
        const { appState, conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const participants = convention.participants || [];
        const conventionYear = conventionYearOf(convention);

        let found = 0;
        let notFound = 0;
        const nameOf = p => `${p.firstname || ''} ${p.lastname || ''}`.trim();
        this.setState({ syncAll: { running: true, done: 0, total: participants.length, found, notFound, currentName: nameOf(participants[0] || {}) } });
        for (let i = 0; i < participants.length; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            const result = await this.syncParticipant(participants[i], conventionYear);
            if (result.ok) {
                found += 1;
            } else {
                notFound += 1;
            }
            // Progress scene: `done` advances once per shooter, `currentName` is the one now in flight.
            this.setState({ syncAll: { running: true, done: i + 1, total: participants.length, found, notFound, currentName: nameOf(participants[i + 1] || {}) } });
        }

        const { loadAll = () => {} } = this.props;
        await loadAll();

        this.setState({ syncAll: { running: false, done: participants.length, total: participants.length, found, notFound } });
        appState.addToast({
            type: 'success',
            message: `Synkronisert ${found} av ${participants.length} deltakere${notFound > 0 ? ` (${notFound} ikke funnet)` : ''}`,
        }, 6000);
    }

    // Shared by the real report and the dry-run preview. dryRun=true assembles the payload on the
    // backend and returns it (resolvedBreakdown + skipped) WITHOUT posting to NSF or marking the
    // convention reported — so the admin can eyeball the mapping before the real submit.
    runRubicReport = async (dryRun = false) => {
        const { appState, conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;
        const { id: conventionId } = convention;

        const norgesFeltTargetMaxValues = conventionResultStore.getNorgesfeltTargetMaxValues();

        this.setState({ reporting: true, reportingDryRun: dryRun });
        const response = await appState.rubicReportResultsToNSF({ competitionId: conventionId, norgesfeltMax: norgesFeltTargetMaxValues, dryRun });
        const { included = {} } = response;
        const {
            rubicReportHtml, rubicReport = [], skippedResults = [], rubicResponse = {},
            resolvedBreakdown = [], competition = {}, counts = {}, reported, rejectedByRubic = [],
        } = included;
        // The backend tells us definitively whether NSF accepted the batch (2xx). Fall back to a
        // status check for older responses that predate the `reported` flag.
        const reportedOk = typeof reported === 'boolean'
            ? reported
            : (rubicResponse.status >= 200 && rubicResponse.status < 300);
        // Turn a 400 rejection body into readable rows (shooter name + reason) for a persistent panel.
        const rubicReportError = (!dryRun && !reportedOk)
            ? parseRubicReportErrors(rubicResponse, response.data)
            : null;

        if (rubicReportHtml) {
            this.setState({ rubicReportHtml });
        }
        this.setState({
            // Only claim results were transferred when NSF actually accepted them.
            rubicReportedCount: (dryRun || !reportedOk) ? 0 : rubicReport.length,
            rubicSkippedResults: skippedResults,
            // Rows NSF refused (e.g. shooter no longer a member of the reported club). The backend
            // drops them and re-posts the rest, so the batch can still be reported OK with these
            // left over for manual registration in Rubic.
            rubicRejectedByRubic: rejectedByRubic,
            rubicResolvedBreakdown: resolvedBreakdown,
            rubicPreview: dryRun ? { competition, counts } : null,
            rubicReportError,
        });

        if (dryRun) {
            appState.addToast({
                type: 'info',
                message: `Dry-run: ${counts.willPost || rubicReport.length} results ready, ${skippedResults.length} skipped — nothing sent to NSF.`,
            }, 5000);
        } else if (reportedOk) {
            appState.addToast({
                type: rejectedByRubic.length > 0 ? 'warning' : 'success',
                message: rejectedByRubic.length > 0
                    ? `Rubic results reported (${rubicReport.length} rows), but NSF refused ${rejectedByRubic.length}. See details below.`
                    : `Rubic results reported successfully! ${rubicResponse.status}: ${rubicResponse.data}`,
            }, rejectedByRubic.length > 0 ? 8000 : 5000);
        } else {
            appState.addToast({
                type: 'error',
                message: `Error reporting results to NSF (${rubicResponse.status} ${rubicResponse.statusText || ''}). See details below.`,
            }, 8000);
        }
        this.setState({ reporting: false });
    }

    reportResultsToRubic = () => this.runRubicReport(false);

    previewResultsToRubic = () => this.runRubicReport(true);

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ search });

        clearTimeout(this.searchTimer);
        if (search.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch(search);
            }, 300);
        }
    }

    async doSearch(search) {
        const { userStore } = this.props.stores;
        if (search) {
            // console.log('doUpdateField.saveField', id, field, value);
            const result = await userStore.searchUsersAll({ search, showAll: 1 });
        } else {
            userStore.localUpdateField('searchUsersResult', []);
        }
    }

    clearSearch = () => {
        this.setState({ search: '' });
        this.doSearch('');
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch(this.state.search);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
        }
    }

    adjustPaidStarts = async (e) => {
        const button = e.target.closest('button');
        const participantMd5 = button.dataset.md5;
        const delta = parseInt(button.dataset.delta, 10);
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const participant = convention?.participants?.find(p => p.md5 === participantMd5);
        if (!participant) {
            return;
        }
        const current = parseInt(participant.paidStartsCount, 10) || 0;
        const newValue = Math.max(0, current + delta);
        if (newValue === current) {
            return;
        }
        await conventionStore.editArrayObject({
            field: 'participants',
            key: 'paidStartsCount',
            value: newValue,
            type: 'number',
            id: convention.id,
            md5: participantMd5,
            objectName: 'convention',
        });
    }

    resolveDisciplineFromClassCode = {
        fin: 'Finfelt',
        grov: 'Grovfelt',
        mil: 'Militærfelt',
        milrp: 'Militærfelt-Rødpunkt',
        rev: 'Revolverfelt',
        revrp: 'Revolverfelt-Rødpunkt',
        'sp.rev': 'Spesialrevolver',
        'sp.pistol': 'Spesialpistol',
        mag1: 'Magnumfelt 1',
        mag2: 'Magnumfelt 2',
        luft: 'Luft',
    };

    getReportValidation = () => {
        const { conventionStore } = this.props.stores;
        const { convention, disciplineMap = [] } = conventionStore;
        const warnings = [];
        if (!convention || !convention.stages) {
            return warnings;
        }

        const participantsById = {};
        (convention.participants || []).forEach((p) => {
            participantsById[p.id] = p;
        });
        const participantName = p => `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Ukjent skytter';

        // Only validate discipline mapping when the Rubic discipline list is loaded
        // AND the per-stage participants actually carry a class code on the frontend.
        const hasClassData = convention.stages.some((stage, idx) =>
            (convention[`participants${idx + 1}`] || []).some(sp => typeof sp.class === 'string' && sp.class.length > 0));
        const canCheckDiscipline = disciplineMap.length > 0 && hasClassData;

        const missingRubic = new Map();
        const unknownDiscipline = new Map();

        convention.stages.forEach((stage, idx) => {
            const stageParticipants = convention[`participants${idx + 1}`] || [];
            stageParticipants.forEach((sp) => {
                const participant = participantsById[sp.id] || sp;
                if (!participant.rubicId) {
                    missingRubic.set(participant.id, participant);
                }
                if (canCheckDiscipline) {
                    const disciplineName = this.resolveDisciplineFromClassCode[sp.class];
                    const matched = disciplineName && disciplineMap.some(d => d.name === disciplineName);
                    if (!matched) {
                        unknownDiscipline.set(`${participant.id}-${sp.class || 'none'}`, { participant, classCode: sp.class });
                    }
                }
            });
        });

        missingRubic.forEach((p) => {
            warnings.push({ type: 'rubicId', name: participantName(p), club: p.sportsClub });
        });
        unknownDiscipline.forEach(({ participant, classCode }) => {
            warnings.push({ type: 'discipline', name: participantName(participant), club: participant.sportsClub, classCode });
        });
        return warnings;
    }

    renderSkipReason = (reason) => {
        if (reason && reason.indexOf('rubicId') > -1) {
            return <Text id='convention.results-skip-reason-rubicid'>not linked to NSF/Rubic (missing SkytterID)</Text>;
        }
        if (reason && reason.indexOf('disciplineId') > -1) {
            return <Text id='convention.results-skip-reason-discipline'>class could not be matched to an NSF discipline</Text>;
        }
        if (reason && reason.indexOf('classId') > -1) {
            return <Text id='convention.results-skip-reason-class'>class could not be matched to an NSF class</Text>;
        }
        return reason;
    }

    // Audits all entered scores for the convention against the per-stand max values
    // (standplass / stageStands). On-demand: loads the full result set, then runs the
    // pure validateConventionScores helper and stores the outcome in local state.
    validateAllScores = async () => {
        const { conventionStore, conventionResultStore } = this.props.stores;
        const { convention } = conventionStore;
        if (!convention || !convention.id) {
            return;
        }
        this.setState({ scoreValidating: true, scoreValidationError: false });
        try {
            await conventionResultStore.load({ query: { convention: convention.id }, limit: 50000 });
            const conventionResults = conventionResultStore.conventionResults || [];
            const scoreValidation = validateConventionScores(convention, conventionResults);
            this.setState({ scoreValidation, scoreValidationRun: true, scoreValidating: false });
        } catch (err) {
            this.setState({ scoreValidating: false, scoreValidationError: true, scoreValidationRun: true });
        }
    }

    scoreFieldLabel = (field) => {
        if (field === 'scoreHits') return <Text id='convention.score-field-hits'>Hits</Text>;
        if (field === 'scoreTargets') return <Text id='convention.score-field-targets'>Targets</Text>;
        if (field === 'scoreInner') return <Text id='convention.score-field-inner'>Inner hits</Text>;
        return field;
    }

    render() {
        const {
            searchTitle,
        } = this.props;
        const {
            search,
            loading = false,
            loadingUser = {},
            reporting = false,
            reportingDryRun = false,
            rubicReportHtml,
            rubicReportedCount,
            rubicSkippedResults = [],
            rubicRejectedByRubic = [],
            rubicResolvedBreakdown = [],
            rubicPreview = null,
            rubicReportError = null,
            scoreValidation = null,
            scoreValidating = false,
            scoreValidationError = false,
            scoreValidationRun = false,
        } = this.state;
        const { isInThePast, isConventionAdmin } = this.props;
        const { appState, userStore, conventionStore, conventionResultStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary, language, searchUsersResult, customer } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { convention, disciplineMap = [], classMap = [] } = conventionStore;
        const { rubicOrganizationId } = customer;

        // Shooter classifications (nsfDiciplineClasses) carry one row per discipline
        // per year; show only the current year's class per weapon class.
        const conventionYear = conventionYearOf(convention);
        const disciplineOrder = disciplineId => {
            const idx = disciplineMap.findIndex(d => d.id === disciplineId);
            return idx === -1 ? disciplineMap.length : idx;
        };
        const { syncAll = {} } = this.state;

        const isConventionIntegrated = rubicOrganizationId && convention.nsfNumber && convention.nsfId;
        const reportValidation = isConventionIntegrated ? this.getReportValidation() : [];
        const hasSignupProduct = convention.signupProduct > 0;
        const participantStageCounts = {};
        if (convention && convention.stages && convention.stages.length) {
            convention.stages.forEach((stage, idx) => {
                const stageParticipants = convention[`participants${idx + 1}`];
                if (stageParticipants && stageParticipants.length) {
                    stageParticipants.forEach(p => {
                        participantStageCounts[p.id] = (participantStageCounts[p.id] || 0) + 1;
                    });
                }
            });
        }
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();

        return (<>
            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1 mt-5 pt-1 text-danger'>
                <Text id='convention.admin'>Admin</Text>
            </div>
            <div class={`d-flex flex-column mt-0 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} overflow-auto border border-danger shadow`}>
                {isInThePast ? <>
                    <div class='d-flex'>
                        <Text id='convention.in-the-past'>Convention is in the past and closed.</Text>
                    </div>
                </> : <>
                    <div class='d-flex font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.add-new-round'>Add new round here:</Text>
                    </div>
                    <div class='d-flex flex-wrap justify-content-center'>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='nais'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-nais'>New NAIS</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='hurtig'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-hurtig'>New hurtig</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='hurtig-half'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-hurtig-half'>New hurtig-half</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='standard'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-standard'>New standard</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='felt'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-felt'>New felt</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='norgesfelt'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-norgesfelt'>New norgesfelt</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='t-96'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-t-96'>New T-96</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='finpistol'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-finpistol'>New VM-Finpistol</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='grovpistol'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-grovpistol'>New VM-Grovpistol</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='silhuett'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-silhuett'>New Silhuettpistol</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='luftpistol'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-luftpistol'>New Luftpistol 10m</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='skarpskytter'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-skarpskytter'>New Skarpskyttermerke</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='tidsoving'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-tidsoving'>New Tidsøvelse</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='tidskonk'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-tidskonk'>New Tidskonkurranse</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                            onClick={this.newRound}
                            data-type='fri'
                        >
                            <i class='fa-duotone fa-plus' /> <Text id='convention.new-fri'>New Fri (poeng)</Text>
                        </button>
                    </div>

                    <div class='d-flex mt-3 justify-content-center'>
                        <button
                            class={`btn btn-lg btn-outline-success rounded-pill mx-1 my-1`}
                            onClick={this.openBulkGenerateStages}
                        >
                            <i class='fa-duotone fa-layer-plus me-2' /> <Text id='convention.bulk-generate-stages'>Bulk Generate Stages</Text>
                        </button>
                    </div>

                    <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.add-new-participant'>Add new participant:</Text>
                    </div>
                    <div class='d-flex flex-wrap justify-content-center'>
                        <div class='flex-fill input-group'>
                            <div class='input-group-text rounded-pill-left'>
                                <i class='fa-regular fa-magnifying-glass'></i>
                            </div>
                            <input
                                class={`form-control ${!search ? 'rounded-pill-right' : ''}`}
                                type='text'
                                value={search}
                                placeholder={`${searchTitle}`}
                                onInput={this.searchInput}
                                onKeyDown={this.handleKeyDown}
                            />
                            {search && <>
                                <button class='btn btn-danger btn-sm rounded-pill-right' type='button' onClick={this.clearSearch}>
                                    <i class='fa-solid fa-circle-xmark'></i>
                                </button>
                            </>}
                        </div>
                        {searchUsersResult && searchUsersResult.length ? <>
                            <div class='d-flex flex-wrap justify-content-center'>
                                {searchUsersResult.map(user => {
                                    return (<>
                                        <button
                                            class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                                            onClick={this.addNewParticipant}
                                            data-id={user.id}
                                        >
                                            <i class='fa-duotone fa-plus' /> {localUtil.displayName(user)} {user.cellphone}
                                        </button>
                                    </>);
                                })}
                            </div>
                        </> : <></>}
                    </div>
                </>}

                {convention.stages && convention.stages.length > 0 ? <>
                    <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.created-rounds'>Created rounds:</Text>
                    </div>
                    <div class='table-responsive'>
                        <table class='table table-sm table-striped table-hover'>
                            <thead>
                                <tr>
                                    <th scope='col'>#</th>
                                    <th scope='col'><nobr><Text id='convention.round-name'>Name</Text></nobr></th>
                                    <th scope='col'><nobr><Text id='convention.round-type'>Type</Text></nobr></th>
                                    <th scope='col'><nobr><Text id='convention.stage-slots'>Number of slots</Text></nobr></th>
                                    <th scope='col'><nobr><Text id='convention.round-time'>Time</Text></nobr></th>
                                    <th scope='col'>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {convention.stages.map((stage, idx) => (
                                    <tr key={stage.md5}>
                                        <td>{idx + 1}</td>
                                        <td><nobr>{stage.name}</nobr></td>
                                        <td><nobr>{getStageTypeDisplay(stage.type)}</nobr></td>
                                        <td><nobr>{stage.stageSlots > 0 ? stage.stageSlots : '-'}</nobr></td>
                                        <td><nobr>{getStageTimeLabel(stage, convention)}</nobr></td>
                                        <td class='text-end'>
                                            <button
                                                class='btn btn-sm btn-outline-primary rounded-pill'
                                                data-id={convention.id}
                                                data-md5={stage.md5}
                                                data-idx={idx}
                                                onClick={this.editStage}
                                            >
                                                <i class='fa-duotone fa-edit' />
                                            </button>
                                            <button
                                                class='btn btn-sm btn-outline-danger rounded-pill ms-1'
                                                data-md5={stage.md5}
                                                data-name={stage.name}
                                                data-count={(convention[`participants${idx + 1}`] || []).length}
                                                onClick={this.deleteStage}
                                            >
                                                <i class='fa-duotone fa-trash' />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </> : <></>}

                {convention.nsfNumber  ? <>
                    <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.convention-settings'>Convention settings:</Text>
                    </div>
                    <div class='d-flex flex-column justify-content-start'>
                        <div>
                            <span class='font-weight-lighter'><Text id='convention.convention-nsfid'>NSF ID</Text>:</span> {convention.nsfId ? <>
                                <i class='fa-duotone fa-check text-success' />
                            </> : <>
                                <i class='fa-duotone fa-xmark text-danger' />
                            </>} <span class='font-weight-lighter text-muted'>{convention.nsfId}</span>
                        </div>

                        <div><span class='font-weight-lighter'><Text id='convention.convention-nsfnumber'>NSF Number</Text>:</span> {convention.nsfNumber} </div>
                        <div><span class='font-weight-lighter'><Text id='convention.convention-integrated'>Is integrated with NSF</Text>:</span> {isConventionIntegrated ? <i class='fa-duotone fa-check text-success' /> : <i class='fa-duotone fa-xmark text-danger' />}</div>

                        {!convention.nsfNumber > 0 && <>
                            <button
                                class={`btn btn-sm btn-outline-primary rounded-pill mx-1 my-1`}
                                onClick={this.editConvention}
                                data-id={convention.id}
                                data-title={convention.title}
                            >
                                <i class='fa-duotone fa-pen-to-square' /> <Text id='convention.edit-convention'>Edit convention</Text>
                            </button>
                        </>}

                        {/* {!convention.nsfId && convention.nsfNumber && isAdmin && <> */}
                        {convention.nsfNumber > 0 && isAdmin && <>
                            <button
                                disabled={loading}
                                type='button'
                                class='btn btn-lg btn-outline-primary rounded-pill ms-2'
                                onClick={this.getRubicCompetitionByNumber}
                            >
                                <nobr>
                                {loading ? <i class='fa-duotone fa-spinner-third fa-spin me-2' /> : <i class='fa-duotone fa-arrow-right me-2' />}
                                <Text id='dyrejournal.rubic-competition-by-number-load'>Integrate with NSF by convention number</Text>
                                </nobr>
                            </button>
                            {loading && <NsfTransferProgress mode='integrate' nsfNumber={convention.nsfNumber} />}
                        </>}

                    </div>

                    <div class='d-flex justify-content-between align-items-center mt-5 font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.all-participants'>All participants:</Text>
                        {isConventionIntegrated && (
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-primary rounded-pill mb-1'
                                onClick={this.lookupAllUsers}
                                disabled={syncAll.running}
                                title='Hent oppdaterte klasser fra NSF for alle deltakere'
                            >
                                {syncAll.running ? (
                                    <span><i class='fa-duotone fa-sync fa-spin me-1' />{syncAll.done}/{syncAll.total}</span>
                                ) : (
                                    <span><i class='fa-duotone fa-sync me-1' /><Text id='convention.sync-all-users'>Sync all users</Text></span>
                                )}
                            </button>
                        )}
                    </div>
                    {syncAll.running && (
                        <NsfTransferProgress
                            mode='sync'
                            current={syncAll.done}
                            total={syncAll.total}
                            label={syncAll.currentName}
                            found={syncAll.found}
                            notFound={syncAll.notFound}
                        />
                    )}
                    <div class='table-responsive'>
                        <table class='table table-sm table-striped table-hover'>
                            <thead>
                                <tr>
                                    <th scope='col'>#</th>
                                    <th scope='col'>&nbsp;</th>
                                    <th scope='col'>&nbsp;</th>
                                    <th scope='col'><nobr><Text id='convention.participant-name'>Name</Text></nobr></th>
                                    <th scope='col'><nobr><Text id='convention.participant-club'>Club</Text></nobr></th>
                                    <th scope='col'><nobr><Text id='convention.participant-nsfid'>NSF ID</Text></nobr></th>
                                    {hasSignupProduct && (
                                        <th scope='col' class='text-center'>
                                            <nobr><Text id='convention.participant-paid'>Paid</Text></nobr>
                                        </th>
                                    )}
                                    <th scope='col'>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {convention.participants.map((participant, index) => {
                                    const stageCount = participantStageCounts[participant.id] || 0;
                                    // Competitions: one fee per Lag. Trainings: one fee in total.
                                    const feeStarts = requiredStarts(convention, stageCount);
                                    const paidStartsCount = parseInt(participant.paidStartsCount, 10) || 0;
                                    const fullyPaid = hasSignupProduct && feeStarts > 0 && paidStartsCount >= feeStarts;
                                    const partiallyPaid = hasSignupProduct && paidStartsCount > 0 && paidStartsCount < feeStarts;
                                    let paidBadgeClass = 'text-bg-warning';
                                    if (fullyPaid) {
                                        paidBadgeClass = 'text-bg-success';
                                    } else if (partiallyPaid) {
                                        paidBadgeClass = 'text-bg-info';
                                    }
                                    return (
                                        <tr key={participant.id}>
                                            <td>{index + 1}</td>
                                            <td>{participant.rubicId ? <><i class='fa-duotone fa-check text-success' /></> : <><i class='fa-duotone fa-xmark text-danger' /></>}</td>
                                            <td>
                                                {isConventionIntegrated ? <>
                                                    <button
                                                        disabled={loadingUser[participant.id]}
                                                        class={`btn btn-sm btn-outline-primary rounded-pill`}
                                                        onClick={this.lookupUser}
                                                        data-id={participant.id}
                                                    >
                                                        {loadingUser[participant.id] ? <i class='fa-duotone fa-sync fa-spin' /> : <i class='fa-duotone fa-sync' />}
                                                    </button>
                                                </> : <>
                                                    <i class='fa-duotone fa-question text-warning' />
                                                </>}
                                            </td>
                                            <td>
                                                <nobr>{participant.firstname} {participant.lastname}</nobr>
                                                <NotMemberBadge participant={participant} convention={convention} />
                                            </td>
                                            <td><nobr>{participant.sportsClub}</nobr></td>
                                            <td>{participant.nsfId}</td>
                                            {hasSignupProduct && (
                                                <td class='text-center'>
                                                    <div class='btn-group btn-group-sm' role='group'>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-secondary'
                                                            disabled={paidStartsCount <= 0}
                                                            data-md5={participant.md5}
                                                            data-delta='-1'
                                                            onClick={this.adjustPaidStarts}
                                                            title='Fjern betalt start'
                                                        >
                                                            <i class='fa-solid fa-minus' />
                                                        </button>
                                                        <span class={`badge ${paidBadgeClass} d-flex align-items-center px-2`} style='border-radius: 0; font-weight: 500;'>
                                                            <nobr>{paidStartsCount} / {feeStarts}</nobr>
                                                        </span>
                                                        <button
                                                            type='button'
                                                            class='btn btn-outline-secondary'
                                                            data-md5={participant.md5}
                                                            data-delta='1'
                                                            onClick={this.adjustPaidStarts}
                                                            title='Bekreft betalt start'
                                                        >
                                                            <i class='fa-solid fa-plus' />
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                            <td>
                                                <div class='d-flex flex-row flex-wrap'>
                                                    {pickCurrentDisciplineClasses(participant?.nsfDiciplineClasses, conventionYear)
                                                        .sort((a, b) => disciplineOrder(a.disciplineId) - disciplineOrder(b.disciplineId))
                                                        .map((classItem) => {
                                                            const participantDisciplineName = disciplineMap.find((d) => d.id === classItem.disciplineId)?.name;
                                                            const participantClassName = classMap.find((c) => c.id === classItem.classId)?.name;
                                                            return (
                                                                <small key={classItem.disciplineId} class='badge text-bg-info ms-1 mt-1 font-weight-light rounded-pill'>
                                                                    {participantDisciplineName}
                                                                    {participantClassName ? ` - ${participantClassName}` : ''}
                                                                </small>
                                                            );
                                                        })}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.score-validation-title'>Validate scores against standplass max:</Text>
                    </div>
                    <div class='d-flex flex-column justify-content-start'>
                        <div>
                            <button
                                type='button'
                                class='btn btn-sm btn-outline-primary rounded-pill mb-2'
                                onClick={this.validateAllScores}
                                disabled={scoreValidating}
                            >
                                {scoreValidating ? (
                                    <span><i class='fa-duotone fa-sync fa-spin me-1' /><Text id='convention.score-validation-running'>Validating…</Text></span>
                                ) : (
                                    <span><i class='fa-duotone fa-clipboard-check me-1' /><Text id='convention.score-validation-button'>Validate all scores</Text></span>
                                )}
                            </button>
                        </div>
                        <small class='text-body-secondary mb-2'>
                            <Text id='convention.score-validation-help'>Checks every entered score against the per-stand maximum (standplass), applying the special-class cap of 5.</Text>
                        </small>
                        {scoreValidationError && (
                            <div class='alert alert-danger rounded-lg'>
                                <i class='fa-duotone fa-triangle-exclamation me-2' />
                                <Text id='convention.score-validation-error'>Could not load or validate the scores. Please try again.</Text>
                            </div>
                        )}
                        {scoreValidationRun && !scoreValidationError && scoreValidation && (
                            scoreValidation.violations.length === 0 ? (
                                <div class='alert alert-success rounded-lg'>
                                    <i class='fa-duotone fa-circle-check me-2' />
                                    <Text id='convention.score-validation-ok' fields={{ count: scoreValidation.checkedCount }}>
                                        All {scoreValidation.checkedCount} scores are within the standplass max values.
                                    </Text>
                                </div>
                            ) : (
                                <div class='alert alert-warning rounded-lg w-100'>
                                    <div class='fw-bold mb-2'>
                                        <i class='fa-duotone fa-triangle-exclamation me-2' />
                                        <Text id='convention.score-validation-found' fields={{ count: scoreValidation.violations.length }}>
                                            {scoreValidation.violations.length} scores exceed the standplass max:
                                        </Text>
                                    </div>
                                    <div class='table-responsive'>
                                        <table class='table table-sm table-striped'>
                                            <thead>
                                                <tr>
                                                    <th scope='col'><nobr><Text id='convention.score-validation-col-shooter'>Shooter</Text></nobr></th>
                                                    <th scope='col'><nobr><Text id='convention.score-validation-col-stage'>Stage</Text></nobr></th>
                                                    <th scope='col'><nobr><Text id='convention.score-validation-col-stand'>Stand</Text></nobr></th>
                                                    <th scope='col'><nobr><Text id='convention.score-validation-col-issue'>Issue</Text></nobr></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {scoreValidation.violations.map((v, i) => (
                                                    <tr key={`${v.participant}-${v.stageIdx}-${v.stand}-${v.field}-${i}`}>
                                                        <td>
                                                            <nobr>{v.participantName}</nobr>
                                                            {v.club ? <span class='text-body-secondary'> ({v.club})</span> : ''}
                                                        </td>
                                                        <td><nobr>{v.stageName}</nobr></td>
                                                        <td>{v.stand}</td>
                                                        <td>
                                                            <nobr>{this.scoreFieldLabel(v.field)} <strong>{v.value}</strong> &gt; <Text id='convention.score-validation-max'>max</Text> {v.max}</nobr>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {scoreValidation.unmatchedCount > 0 && (
                                        <small class='text-body-secondary'>
                                            <Text id='convention.score-validation-unmatched' fields={{ count: scoreValidation.unmatchedCount }}>
                                                {scoreValidation.unmatchedCount} score rows could not be matched to a stage and were skipped.
                                            </Text>
                                        </small>
                                    )}
                                </div>
                            )
                        )}
                    </div>


                    {!!isConventionIntegrated && <>
                        <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                            <Text id='convention.results-prevalidation'>Pre-check before reporting to NSF:</Text>
                        </div>
                        <div class='d-flex flex-column justify-content-start'>
                            {!convention.rubicReported && <>
                                <div class='alert alert-light border rounded-lg'>
                                    <i class='fa-duotone fa-circle-info me-2' />
                                    <Text id='convention.results-prevalidation-not-reported'>Results have not been reported to NSF yet.</Text>
                                </div>
                            </>}
                            {reportValidation.length === 0 ? <>
                                <div class='alert alert-success rounded-lg'>
                                    <i class='fa-duotone fa-circle-check me-2' />
                                    <Text id='convention.results-prevalidation-ok'>All participants are ready to be transferred to NSF.</Text>
                                </div>
                            </> : <>
                                <div class='alert alert-warning rounded-lg w-100' style={{ maxWidth: '600px' }}>
                                    <div class='fw-bold mb-2'>
                                        <i class='fa-duotone fa-triangle-exclamation me-2' />
                                        <Text id='convention.results-prevalidation-title' fields={{ count: reportValidation.length }}>
                                            {reportValidation.length} issues may prevent results from being transferred:
                                        </Text>
                                    </div>
                                    <ul class='mb-0 ps-3'>
                                        {reportValidation.map(w => (
                                            <li>
                                                <strong>{w.name}</strong>{w.club ? ` (${w.club})` : ''}
                                                <span class='text-body-secondary'> · {w.type === 'rubicId'
                                                    ? <Text id='convention.results-skip-reason-rubicid'>not linked to NSF/Rubic (missing SkytterID)</Text>
                                                    : <Text id='convention.results-prevalidation-discipline' fields={{ class: w.classCode || '-' }}>discipline "{w.classCode || '-'}" could not be matched to NSF</Text>}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>}
                        </div>
                    </>}

                    <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                        <Text id='convention.convention-results-reporting'>Convention results reporting:</Text>
                    </div>
                    <div class='d-flex flex-column justify-content-start'>
                        <a
                            href={`${apiServer}/api/conventions/norgesfelt/${convention.id}/download?jwtToken=${jwtToken}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            class='btn btn-sm btn-outline-danger btn-block rounded-pill me-2 mb-2'
                        >
                            <i class='fas fa-download me-2' /><Text id='convention.results-download-norgesfelt-csv'>Download Norgesfelt CSV</Text>
                        </a>
                        <a
                            href={`${apiServer}/api/conventions/norgesfelt/${convention.id}/download?format=xlsx&jwtToken=${jwtToken}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            class='btn btn-sm btn-outline-success btn-block rounded-pill me-2 mb-2'
                        >
                            <i class='fas fa-file-excel me-2' /><Text id='convention.results-download-norgesfelt-xlsx'>Download Norgesfelt Excel</Text>
                        </a>
                    </div>
                    <div class='d-flex flex-column justify-content-center align-items-center'>
                        {isConventionIntegrated ? <>
                            <div class='d-flex flex-row justify-content-center align-items-center flex-wrap'>
                                <button
                                    class='btn btn-lg btn-outline-secondary rounded-pill me-2 mb-2'
                                    onClick={this.previewResultsToRubic}
                                    disabled={reporting}
                                >
                                    {reporting
                                        ? <span class='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                                        : <i class='fa-duotone fa-flask me-2' />}
                                    <Text id='convention.results-preview-nsf'>Preview (dry-run)</Text>
                                </button>
                                <button
                                    class='btn btn-lg btn-danger rounded-pill me-2 mb-2'
                                    onClick={this.reportResultsToRubic}
                                    disabled={reporting}
                                >
                                    {reporting
                                        ? <span class='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                                        : <i class='fas fa-arrow-right me-2' />}
                                    <Text id='convention.results-report-nsf'>Report results to NSF</Text>
                                </button>
                            </div>
                            {reporting && (
                                <div class='w-100' style='max-width: 900px;'>
                                    <NsfTransferProgress mode='report' dryRun={reportingDryRun} />
                                </div>
                            )}
                        </> : <>
                            <Text id='convention.results-report-nsf-not-integrated'>Convention is not integrated with NSF, cannot report results.</Text>
                        </>}
                        {convention.rubicReported && <>
                            <div class='alert alert-success rounded-lg'>
                                <i class='fa-duotone fa-check me-2' />
                                <Text id='convention.results-reported-nsf'>Results have been reported to NSF.</Text> {convention.rubicReportDate && <span class='text-muted'>({util.formatDate(convention.rubicReportDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })})</span>}
                            </div>
                        </>}
                        {rubicReportedCount > 0 && <>
                            <div class='alert alert-info rounded-lg'>
                                <i class='fa-duotone fa-paper-plane me-2' />
                                <Text id='convention.results-reported-count' fields={{ count: rubicReportedCount }}>
                                    {rubicReportedCount} results were transferred to NSF.
                                </Text>
                            </div>
                        </>}
                        {rubicReportError && <>
                            <div class='alert alert-danger rounded-lg w-100' style={{ maxWidth: '700px' }}>
                                <div class='fw-bold mb-2'>
                                    <i class='fa-duotone fa-circle-xmark me-2' />
                                    <Text id='convention.results-report-error-title' fields={{ status: rubicReportError.status }}>
                                        NSF rejected the report (HTTP {rubicReportError.status}) — nothing was registered.
                                    </Text>
                                </div>
                                {rubicReportError.rows.length > 0 ? <>
                                    <div class='mb-2'>
                                        <Text id='convention.results-report-error-intro'>Fix these shooters, then report again:</Text>
                                    </div>
                                    <ul class='mb-0 ps-3'>
                                        {rubicReportError.rows.map(row => (
                                            <li>
                                                <strong>{row.name || row.personId}</strong>{row.club ? ` (${row.club})` : ''}
                                                <span class='text-body-secondary'> · {row.message}{row.property ? ` [${row.property}]` : ''}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </> : <>
                                    <div class='small text-break'>
                                        <code>{rubicReportError.raw}</code>
                                    </div>
                                </>}
                            </div>
                        </>}
                        {rubicSkippedResults.length > 0 && <>
                            <div class='alert alert-warning rounded-lg w-100' style={{ maxWidth: '600px' }}>
                                <div class='fw-bold mb-2'>
                                    <i class='fa-duotone fa-triangle-exclamation me-2' />
                                    <Text id='convention.results-skipped-title' fields={{ count: rubicSkippedResults.length }}>
                                        {rubicSkippedResults.length} shooters were NOT transferred to NSF:
                                    </Text>
                                </div>
                                <ul class='mb-0 ps-3'>
                                    {rubicSkippedResults.map(skipped => (
                                        <li>
                                            <strong>{skipped.name}</strong> ({skipped.club}) — {skipped.class}
                                            <span class='text-body-secondary'> · {this.renderSkipReason(skipped.reason)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>}
                        {rubicRejectedByRubic.length > 0 && <>
                            <div class='alert alert-danger rounded-lg w-100' style={{ maxWidth: '600px' }}>
                                <div class='fw-bold mb-2'>
                                    <i class='fa-duotone fa-circle-xmark me-2' />
                                    <Text id='convention.results-rejected-title' fields={{ count: rubicRejectedByRubic.length }}>
                                        {rubicRejectedByRubic.length} results were REFUSED by NSF and must be registered manually in Rubic:
                                    </Text>
                                </div>
                                <ul class='mb-0 ps-3'>
                                    {rubicRejectedByRubic.map(rejected => (
                                        <li key={`${rejected.personId}-${rejected.disciplineId}`}>
                                            <strong>{rejected.name}</strong> ({rejected.club}) — {rejected.discipline} {rejected.class}
                                            {rejected.position > 0 && <> · #{rejected.position}</>}
                                            {rejected.score > 0 && <> · {rejected.score}</>}
                                            <span class='text-body-secondary'> · {rejected.errorMessage}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>}
                        {rubicResolvedBreakdown.length > 0 && <>
                            <div class='w-100 mt-2' style={{ maxWidth: '900px' }}>
                                {rubicPreview && <>
                                    <div class='alert alert-info rounded-lg'>
                                        <i class='fa-duotone fa-flask me-2' />
                                        <Text id='convention.results-preview-heading'>Dry-run preview — nothing was sent to NSF</Text>
                                        <div class='small mt-1'>
                                            <Text
                                                id='convention.results-preview-summary'
                                                fields={{
                                                    type: rubicPreview.competition?.competitionType || '-',
                                                    willPost: rubicPreview.counts?.willPost || 0,
                                                    skipped: rubicPreview.counts?.skipped || 0,
                                                }}
                                            >
                                                Type: {rubicPreview.competition?.competitionType || '-'} · {rubicPreview.counts?.willPost || 0} to post · {rubicPreview.counts?.skipped || 0} skipped
                                            </Text>
                                        </div>
                                    </div>
                                </>}
                                <div class='table-responsive' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    <table class='table table-sm table-striped align-middle'>
                                        <thead>
                                            <tr>
                                                <th><Text id='convention.results-col-shooter'>Shooter</Text></th>
                                                <th><Text id='convention.results-col-discipline'>Discipline</Text></th>
                                                <th><Text id='convention.results-col-class'>Class</Text></th>
                                                <th class='text-end'><Text id='convention.results-col-pos'>Pos</Text></th>
                                                <th class='text-end'><Text id='convention.results-col-score'>Score</Text></th>
                                                <th class='text-end'><Text id='convention.results-col-ranking'>Ranking</Text></th>
                                                {!!convention.isNsfClassLeading && <th class='text-center'><Text id='convention.results-col-opprykk'>Opprykk</Text></th>}
                                                <th class='text-center'><Text id='convention.results-col-post'>Post?</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rubicResolvedBreakdown.map(r => (
                                                <tr class={r.willPost ? '' : 'table-warning'}>
                                                    <td>
                                                        {r.name}
                                                        {r.club ? <span class='text-body-secondary'> · {r.club}</span> : ''}
                                                        {r.skytterId ? <span class='text-body-secondary small'> · {r.skytterId}</span> : ''}
                                                    </td>
                                                    <td>{r.discipline || <span class='text-danger'>—</span>}</td>
                                                    <td>
                                                        {r.class || <span class='text-danger'>—</span>}
                                                        {r.classCode ? <span class='text-body-secondary'> [{r.classCode}]</span> : ''}
                                                    </td>
                                                    <td class='text-end'>{r.position}</td>
                                                    <td class='text-end'>{r.score}{r.max > 0 ? <span class='text-body-secondary'> / {r.max}</span> : ''}</td>
                                                    <td class='text-end'>{r.rankingScore}</td>
                                                    {!!convention.isNsfClassLeading && <td class='text-center'>{r.qualifiesFor || ''}</td>}
                                                    <td class='text-center'>
                                                        {r.willPost
                                                            ? <i class='fa-duotone fa-check text-success' />
                                                            : <i class='fa-duotone fa-xmark text-danger' />}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>}
                    </div>
                </> : <></>}

                {rubicReportHtml && <>
                    <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2 align-items-center justify-content-between'>
                        <Text id='convention.rubic-report'>Rubic report:</Text>
                        <a
                            class='btn btn-sm btn-outline-secondary rounded-pill mb-1'
                            target='_blank'
                            rel='noreferrer'
                            href={`${apiServer}/api/integrations/rubic/results/report/pdf?competitionId=${convention.id}&jwtToken=${jwtToken}`}
                        >
                            <i class='fa-duotone fa-file-pdf me-2' /><Text id='convention.rubic-report-download-pdf'>Download result list (PDF)</Text>
                        </a>
                    </div>
                    <div class='d-flex flex-column justify-content-start'>
                        <div class='p-3 rounded-lg shadow-sm' style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            <iframe
                                srcDoc={rubicReportHtml}
                                style={{ width: '100%', height: '400px', border: 'none' }}
                                title='Rubic Report'
                            />
                        </div>
                    </div>
                </>}

                {/* Access explanation: who can do what on this convention. Keep in
                    sync with isConventionOfficial (backend/lib/convention-access.js)
                    and the pill gating in convention/view.js. */}
                <div class='d-flex mt-5 font-weight-lighter border-bottom mb-2'>
                    Hvem har tilgang til hva?
                </div>
                <div class='table-responsive'>
                    <table class='table table-sm table-striped align-middle' style='font-size: 0.85rem;'>
                        <thead>
                            <tr>
                                <th>Rolle</th>
                                <th class='text-center'><nobr>Administrere stevnet</nobr></th>
                                <th class='text-center'><nobr>Føre score (alle)</nobr></th>
                                <th class='text-center'><nobr>Crew-meldinger</nobr></th>
                                <th class='text-center'><nobr>Føre egen score</nobr></th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { role: 'Systemadmin / kundeadmin', note: '', admin: true, score: true, crew: true, own: true },
                                { role: 'Stevnets eier', note: 'Brukeren som opprettet stevnet', admin: true, score: true, crew: true, own: true },
                                { role: 'Medlemmer-listen', note: '«Medlemmer» i Rediger stevne', admin: true, score: true, crew: true, own: true },
                                { role: 'Arrangør-listen', note: '«Arr.»-knappen på stevnesiden', admin: false, score: true, crew: true, own: true },
                                { role: 'Påmeldte deltakere', note: '', admin: false, score: false, crew: false, own: true },
                            ].map(row => (
                                <tr key={row.role}>
                                    <td>
                                        {row.role}
                                        {row.note && <div class='small text-body-secondary'>{row.note}</div>}
                                    </td>
                                    {[row.admin, row.score, row.crew, row.own].map(allowed => (
                                        <td class='text-center'>
                                            {allowed
                                                ? <i class='fa-duotone fa-check text-success' />
                                                : <i class='fa-duotone fa-xmark text-body-tertiary' />}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <small class='text-body-secondary mb-4'>
                    Medlems- og arrangørlisten matches mot innlogget bruker på bruker-id, telefonnummer eller e-post.
                    Arrangører får Score- og Meldinger-knappene på stevnesiden, og kan også håndtere målfoto og
                    skyttermerknader. «Føre egen score» gjelder ikke stevner der kun arrangøren fører score.
                    Brukere med «Godkjent»-flagget i brukeradmin kan i tillegg håndtere målfoto på alle stevner.
                </small>

            </div>
        </>);
    }
}
export default ConventionViewStageAdmin;
